<template>
  <div>
    <HrPageLayout
      title="Terminations"
      subtitle="Manage employee terminations and separation"
      icon="📋"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'Separation' },
        { label: 'Terminations' }
      ]"
    >
      <template #header-actions>
        <select class="hr-select sep-month-select" aria-label="Select Month" @change="handleMonthChange" v-model="selectedMonth">
          <option value="">Select Month</option>
          <option value="1">January</option>
          <option value="2">February</option>
          <option value="3">March</option>
          <option value="4">April</option>
          <option value="5">May</option>
          <option value="6">June</option>
          <option value="7">July</option>
          <option value="8">August</option>
          <option value="9">September</option>
          <option value="10">October</option>
          <option value="11">November</option>
          <option value="12">December</option>
        </select>
        <button type="button" class="hr-btn hr-btn-primary" @click="openDownloadModal">
          <i class="bx bxs-download"></i> Download Report
        </button>
        <button type="button" class="hr-btn hr-btn-success" @click="openAddTerminationModal">
          <i class="bx bx-plus"></i> Create Termination
        </button>
        <button type="button" class="hr-btn hr-btn-info" @click="generateClearanceReport">
          <i class="bx bx-check-circle"></i> Clearance Report
        </button>
      </template>

      <div class="separation-content">
        <!-- Message toast (inline) -->
        <div v-if="message" class="sep-toast" :class="message.includes('success') ? 'success' : 'error'">
          <strong>{{ message.includes('success') ? 'Success' : 'Error' }}</strong>
          <span>{{ message }}</span>
          <button type="button" class="sep-toast-close" @click="message = ''">&times;</button>
        </div>

        <!-- Stats -->
        <div class="hr-metrics-grid hr-mb-4">
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-user-x"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Total Terminations</p>
            <h2 class="hr-metric-value">{{ statistics.totalTerminations }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-2">
            <div class="hr-metric-icon"><i class="bx bx-log-out"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Resignations</p>
            <h2 class="hr-metric-value">{{ statistics.resignations }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-block"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Dismissals</p>
            <h2 class="hr-metric-value">{{ statistics.dismissals }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-1">
            <div class="hr-metric-icon"><i class="bx bx-transfer"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Transfers</p>
            <h2 class="hr-metric-value">{{ statistics.transfers }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-sad"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Death</p>
            <h2 class="hr-metric-value">{{ statistics.deaths }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-4">
            <div class="hr-metric-icon"><i class="bx bx-help-circle"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Other</p>
            <h2 class="hr-metric-value">{{ statistics.others }}</h2>
            </div>
          </div>
          <div class="hr-metric-card gradient-3">
            <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
            <div class="hr-metric-content">
              <p class="hr-metric-label">Pending</p>
            <h2 class="hr-metric-value">{{ statistics.pending }}</h2>
            </div>
          </div>
        </div>

        <!-- Table card -->
        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <div class="sep-filters">
              <div class="hr-search-wrap">
                <input type="text" class="hr-input" placeholder="Search..." @input="handleSearch">
                <i class="bx bx-search-alt search-icon"></i>
              </div>
              <div class="hr-filter-group">
                <label class="hr-form-label">Department</label>
                <select class="hr-select" style="width: 200px;" v-model="selectedDepartment" @change="handleDepartmentChange">
                  <option value="">All Departments</option>
                  <option v-for="department in departments" :key="department.id" :value="department.id">
                    {{ department.departmentName }}
                  </option>
                </select>
              </div>
            </div>
          </div>
          <div class="hr-chart-body">
            <div v-if="isLoading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p>Loading...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover hr-table-striped">
                <thead>
                  <tr>
                    <th>Employee Name</th>
                    <th>Separation Type</th>
                    <th>Termination Date</th>
                    <th>Last Working Day</th>
                    <th>Description</th>
                    <th>Department</th>
                    <th>Status</th>
                    <th>Documents</th>
                    <th>Clearance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in terminationsData" :key="item.id">
                    <td>
                      <div>{{ item.employeeName || 'N/A' }}</div>
                      <small class="hr-text-muted">ID: {{ item.employeeId }}</small>
                    </td>
                    <td>
                      <span class="hr-badge" :class="getSeparationTypeBadgeClass(item.separationType)">
                        {{ item.separationType }}
                      </span>
                    </td>
                    <td>{{ formatDate(item.terminationDate) }}</td>
                    <td>{{ formatDate(item.lastWorkingDay) }}</td>
                    <td>
                      <div class="sep-cell-truncate" :title="item.description">{{ item.description }}</div>
                    </td>
                    <td>
                      <div>{{ item.departmentName }}</div>
                      <small class="hr-text-muted" v-if="item.departmentID">ID: {{ item.departmentID }}</small>
                    </td>
                    <td>
                      <span :class="'hr-badge ' + getStatusBadgeClass(item.status)">{{ formatStatus(item.status) }}</span>
                      <div class="hr-text-muted hr-small hr-mt-1" v-if="item.status && item.status.toLowerCase() === 'approved' && item.approvedAt">
                        {{ formatDateWithTime(item.approvedAt) }}
                      </div>
                      <div class="hr-text-muted hr-small hr-mt-1" v-if="item.status && item.status.toLowerCase() === 'rejected' && item.rejectedAt">
                        {{ formatDateWithTime(item.rejectedAt) }}
                      </div>
                    </td>
                    <td>
                      <template v-if="item.documents && item.documents.length > 0">
                        <div class="sep-doc-cell">
                          <span class="hr-badge hr-badge-info">{{ item.documents.length }} document{{ item.documents.length > 1 ? 's' : '' }}</span>
                          <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" @click="viewDocuments(item.documents)">
                            <i class="bx bx-file"></i> View All
                          </button>
                        </div>
                      </template>
                      <span v-else>No documents</span>
                    </td>
                    <td>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-info" @click="viewClearanceStatus(item)">
                        <i class="bx bx-check-circle"></i> View Status
                      </button>
                    </td>
                    <td>
                      <div class="sep-actions-cell">
                        <button v-if="!item.status || item.status.toLowerCase() === 'pending'"
                                type="button" class="hr-btn hr-btn-sm hr-btn-success" @click="showConfirmationModal(item.id, 'approve')">
                          Approve
                        </button>
                        <button v-if="!item.status || item.status.toLowerCase() === 'pending'"
                                type="button" class="hr-btn hr-btn-sm hr-btn-danger" @click="showConfirmationModal(item.id, 'reject')">
                          Reject
                        </button>
                        <span v-if="item.status && item.status.toLowerCase() !== 'pending'" class="hr-text-muted hr-small">
                          {{ item.status.toLowerCase() === 'approved' ? 'Approved by ' + (item.approvedBy || 'Admin') : '' }}
                          {{ item.status.toLowerCase() === 'rejected' ? 'Rejected by ' + (item.rejectedBy || 'Admin') : '' }}
                        </span>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="terminationsData.length === 0">
                    <td colspan="10" class="hr-text-center hr-py-4 hr-text-muted">No termination records found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <!-- Create Termination Modal (pure Vue, no Bootstrap JS) -->
      <div
          v-if="showCreateTerminationModal"
          class="modal fade show"
          style="display:block; background: rgba(0,0,0,0.5);"
      >
        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="addTerminationModalLabel">Create Termination</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="closeAddTerminationModal"></button>
            </div>
            <div class="modal-body">
              <form id="addTerminationForm" @submit="submitTermination">
                <div class="row">
                  <!-- Select Employee -->
                  <div class="col-md-6 mb-3">
                    <label for="selectEmployee" class="form-label">Select Employee</label>
                    <select
                        class="form-control"
                        id="selectEmployee"
                        name="selectEmployee"
                        v-model="selectedEmployeeId"
                        @change="onEmployeeChange"
                        required
                    >
                      <option value="">Select Employee</option>
                      <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                        {{ employee.name }}
                      </option>
                    </select>
                    <div v-if="selectedEmployeeDetails" class="mt-2 text-muted small">
                      <div><strong>Department:</strong> {{ selectedEmployeeDetails.department || 'N/A' }}</div>
                      <div><strong>Designation:</strong> {{ selectedEmployeeDetails.designation || 'N/A' }}</div>
                    </div>
                  </div>
                  <!-- Separation Type -->
                  <div class="col-md-6 mb-3">
                    <label for="separationType" class="form-label">Separation Type</label>
                    <select class="form-control" id="separationType" name="separationType" required>
                      <option value="">Select separation type</option>
                      <option value="Resignation">Resignation</option>
                      <option value="Death">Death</option>
                      <option value="Transfers">Transfers</option>
                      <option value="Dismissal">Dismissal</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <!-- Separation Date -->
                  <div class="col-md-6 mb-3">
                    <label for="separationDate" class="form-label">Separation Date</label>
                    <input
                        type="date"
                        class="form-control"
                        id="separationDate"
                        name="separationDate"
                        :value="new Date().toISOString().substr(0, 10)"
                        required
                    >
                  </div>
                  <!-- Last Working Day -->
                  <div class="col-md-6 mb-3">
                    <label for="lastWorkingDay" class="form-label">Last Working Day</label>
                    <input
                        type="date"
                        class="form-control"
                        id="lastWorkingDay"
                        name="lastWorkingDay"
                        :value="new Date().toISOString().substr(0, 10)"
                        required
                    >
                  </div>
                  <!-- Description -->
                  <div class="col-md-12 mb-3">
                    <label for="description" class="form-label">Description</label>
                    <textarea
                        class="form-control"
                        id="description"
                        name="description"
                        rows="3"
                        placeholder="Enter Description"
                    ></textarea>
                  </div>

                  <!-- Clearance from Company Section -->
                  <div class="col-md-12 mb-3">
                    <div class="card">
                      <div class="card-header bg-light">
                        <h6 class="mb-0">Company Clearance</h6>
                      </div>
                      <div class="card-body">
                        <div class="row">
                          <div class="col-md-6">
                            <div class="mb-3">
                              <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="itClearance" name="itClearance">
                                <label class="form-check-label" for="itClearance">
                                  IT Department Clearance
                                </label>
                              </div>
                              <small class="text-muted">Returned all company devices, access cards, etc.</small>
                            </div>

                            <div class="mb-3">
                              <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="financeClearance" name="financeClearance">
                                <label class="form-check-label" for="financeClearance">
                                  Finance Department Clearance
                                </label>
                              </div>
                              <small class="text-muted">Settled all pending accounts, advances, etc.</small>
                            </div>

                            <div class="mb-3">
                              <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="hrClearance" name="hrClearance">
                                <label class="form-check-label" for="hrClearance">
                                  HR Department Clearance
                                </label>
                              </div>
                              <small class="text-muted">Completed exit interview, handover notes, etc.</small>
                            </div>
                          </div>

                          <div class="col-md-6">
                            <div class="mb-3">
                              <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="adminClearance" name="adminClearance">
                                <label class="form-check-label" for="adminClearance">
                                  Admin Department Clearance
                                </label>
                              </div>
                              <small class="text-muted">Returned office keys, parking cards, etc.</small>
                            </div>

                            <div class="mb-3">
                              <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="deptClearance" name="deptClearance">
                                <label class="form-check-label" for="deptClearance">
                                  Department Head Clearance
                                </label>
                              </div>
                              <small class="text-muted">Completed knowledge transfer, handover, etc.</small>
                            </div>

                            <div class="mb-3">
                              <div class="form-check">
                                <input class="form-check-input" type="checkbox" id="securityClearance" name="securityClearance">
                                <label class="form-check-label" for="securityClearance">
                                  Security Department Clearance
                                </label>
                              </div>
                              <small class="text-muted">Returned ID badge, security tokens, etc.</small>
                            </div>
                          </div>
                        </div>

                        <div class="row mt-2">
                          <div class="col-md-12">
                            <div class="mb-3">
                              <label for="clearanceNotes" class="form-label">Clearance Notes</label>
                              <textarea
                                  class="form-control"
                                  id="clearanceNotes"
                                  name="clearanceNotes"
                                  rows="2"
                                  placeholder="Enter any specific notes about clearance status"
                              ></textarea>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Documents -->
                  <div class="col-md-12 mb-3">
                    <label for="uploadDocuments" class="form-label">Upload Documents</label>
                    <input type="file" class="form-control" id="uploadDocuments" name="uploadDocuments" multiple>
                    <small class="text-muted">Upload resignation letter, clearance forms, exit interview, etc.</small>
                  </div>
                </div>
                <!-- Message Alert -->
                <div v-if="message" class="alert" :class="message.includes('successfully') ? 'alert-success' : 'alert-danger'">
                  {{ message }}
                </div>
                <!-- Modal Footer -->
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary" @click="closeAddTerminationModal">Cancel</button>
                  <button type="submit" class="btn btn-primary" :disabled="isLoading">
                    <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    Save Termination
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Teleport to="body">
      <!-- Confirmation Modal -->
      <div class="modal fade sep-term-bs-modal" id="sep-term-confirmation-modal" tabindex="-1" aria-labelledby="confirmationModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header" :class="confirmationType === 'approve' ? 'bg-success text-white' : 'bg-danger text-white'">
              <h5 class="modal-title" id="confirmationModalLabel">
                {{ confirmationType === 'approve' ? 'Approve Termination' : 'Reject Termination' }}
              </h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <p>
                <strong>Are you sure you want to {{ confirmationType === 'approve' ? 'approve' : 'reject' }} this termination?</strong>
              </p>
              <p>This action cannot be undone.</p>

              <!-- Display termination information -->
              <div v-if="selectedTermination" class="card mt-3">
                <div class="card-header bg-light">
                  <h6 class="mb-0">Termination Details</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6">
                      <p class="mb-2"><strong>Employee:</strong> {{ selectedTermination.employeeName || 'N/A' }}</p>
                      <p class="mb-2"><strong>Separation Type:</strong> {{ selectedTermination.separationType }}</p>
                      <p class="mb-2"><strong>Department:</strong> {{ selectedTermination.departmentName }}</p>
                    </div>
                    <div class="col-md-6">
                      <p class="mb-2"><strong>Termination Date:</strong> {{ formatDate(selectedTermination.terminationDate) }}</p>
                      <p class="mb-2"><strong>Last Working Day:</strong> {{ formatDate(selectedTermination.lastWorkingDay) }}</p>
                      <p class="mb-2"><strong>Created:</strong> {{ formatDateWithTime(selectedTermination.createdAt) }}</p>
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12">
                      <p class="mb-1"><strong>Description:</strong></p>
                      <p class="bg-light p-2 rounded">{{ selectedTermination.description }}</p>
                    </div>
                  </div>
                  <div class="row mt-2" v-if="selectedTermination.documents && selectedTermination.documents.length > 0">
                    <div class="col-12">
                      <p class="mb-1"><strong>Documents ({{ selectedTermination.documents.length }}):</strong></p>
                      <ul class="list-group">
                        <li v-for="(doc, index) in selectedTermination.documents" :key="index" class="list-group-item d-flex justify-content-between align-items-center">
                          <span class="text-truncate me-3">{{ doc.documentName }}</span>
                          <a :href="documentUrl(doc.documentPath)" target="_blank" class="btn btn-sm btn-outline-primary">
                            <i class="mdi mdi-eye"></i> View
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
              <button
                  type="button"
                  :class="confirmationType === 'approve' ? 'btn btn-success' : 'btn btn-danger'"
                  :disabled="isProcessingAction"
                  @click="confirmAction">
                <span v-if="isProcessingAction" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                {{ confirmationType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents Viewer Modal -->
      <div class="modal fade sep-term-bs-modal" id="sep-term-documents-modal" tabindex="-1" aria-labelledby="documentsModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="documentsModalLabel">Documents</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div v-if="currentDocuments && currentDocuments.length > 0">
                <div class="table-responsive">
                  <table class="table table-bordered table-hover">
                    <thead class="table-light">
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Document Name</th>
                      <th scope="col">Upload Date</th>
                      <th scope="col">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(doc, index) in currentDocuments" :key="index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ doc.documentName }}</td>
                      <td>{{ formatDateWithTime(doc.uploadedAt) }}</td>
                      <td>
                        <div class="d-flex">
                          <a :href="documentUrl(doc.documentPath)" target="_blank" class="btn btn-sm btn-primary me-2">
                            <i class="mdi mdi-eye"></i> View
                          </a>
                          <a :href="documentUrl(doc.documentPath)" download class="btn btn-sm btn-success">
                            <i class="mdi mdi-download"></i> Download
                          </a>
                        </div>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div v-else class="text-center p-4">
                <div class="alert alert-warning">No documents available for this termination.</div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>

      <!-- Clearance Status Modal -->
    <div class="modal fade sep-term-bs-modal" id="sep-term-clearance-modal" tabindex="-1" aria-labelledby="clearanceModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="clearanceModalLabel">
              Clearance Status for {{ selectedTermination ? selectedTermination.employeeName : '' }}
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedTermination && selectedTermination.clearanceInfo">
              <div class="row mb-4">
                <div class="col-12">
                  <div class="d-flex align-items-center justify-content-between mb-3">
                    <h6 class="mb-0">Overall Clearance Status:</h6>
                    <span
                        class="badge py-2 px-3"
                        :class="calculateOverallClearance(selectedTermination.clearanceInfo) >= 100 ? 'bg-success' : 'bg-warning'"
                    >
                      {{ calculateOverallClearance(selectedTermination.clearanceInfo) }}% Complete
                    </span>
                  </div>
                  <div class="progress" style="height: 20px;">
                    <div
                        class="progress-bar"
                        :class="calculateOverallClearance(selectedTermination.clearanceInfo) >= 100 ? 'bg-success' : calculateOverallClearance(selectedTermination.clearanceInfo) >= 70 ? 'bg-info' : 'bg-warning'"
                        role="progressbar"
                        :style="`width: ${calculateOverallClearance(selectedTermination.clearanceInfo)}%`"
                        :aria-valuenow="calculateOverallClearance(selectedTermination.clearanceInfo)"
                        aria-valuemin="0"
                        aria-valuemax="100"
                    >
                      {{ calculateOverallClearance(selectedTermination.clearanceInfo) }}%
                    </div>
                  </div>
                </div>
              </div>

              <div class="row mb-4">
                <div class="col-md-6">
                  <div class="clearance-item mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <div>
                        <i class="mdi mdi-laptop me-1"></i> IT Department Clearance
                      </div>
                      <span class="badge" :class="selectedTermination.clearanceInfo.itClearance ? 'bg-success' : 'bg-danger'">
                        {{ selectedTermination.clearanceInfo.itClearance ? 'Cleared' : 'Pending' }}
                      </span>
                    </div>
                    <small class="text-muted">Returned all company devices, access cards, etc.</small>
                  </div>

                  <div class="clearance-item mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <div>
                        <i class="mdi mdi-cash-multiple me-1"></i> Finance Department Clearance
                      </div>
                      <span class="badge" :class="selectedTermination.clearanceInfo.financeClearance ? 'bg-success' : 'bg-danger'">
                        {{ selectedTermination.clearanceInfo.financeClearance ? 'Cleared' : 'Pending' }}
                      </span>
                    </div>
                    <small class="text-muted">Settled all pending accounts, advances, etc.</small>
                  </div>

                  <div class="clearance-item mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <div>
                        <i class="mdi mdi-account-group me-1"></i> HR Department Clearance
                      </div>
                      <span class="badge" :class="selectedTermination.clearanceInfo.hrClearance ? 'bg-success' : 'bg-danger'">
                        {{ selectedTermination.clearanceInfo.hrClearance ? 'Cleared' : 'Pending' }}
                      </span>
                    </div>
                    <small class="text-muted">Completed exit interview, handover notes, etc.</small>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="clearance-item mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <div>
                        <i class="mdi mdi-office-building me-1"></i> Admin Department Clearance
                      </div>
                      <span class="badge" :class="selectedTermination.clearanceInfo.adminClearance ? 'bg-success' : 'bg-danger'">
                        {{ selectedTermination.clearanceInfo.adminClearance ? 'Cleared' : 'Pending' }}
                      </span>
                    </div>
                    <small class="text-muted">Returned office keys, parking cards, etc.</small>
                  </div>

                  <div class="clearance-item mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <div>
                        <i class="mdi mdi-account-supervisor me-1"></i> Department Head Clearance
                      </div>
                      <span class="badge" :class="selectedTermination.clearanceInfo.deptClearance ? 'bg-success' : 'bg-danger'">
                        {{ selectedTermination.clearanceInfo.deptClearance ? 'Cleared' : 'Pending' }}
                      </span>
                    </div>
                    <small class="text-muted">Completed knowledge transfer, handover, etc.</small>
                  </div>

                  <div class="clearance-item mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-1">
                      <div>
                        <i class="mdi mdi-shield-account me-1"></i> Security Department Clearance
                      </div>
                      <span class="badge" :class="selectedTermination.clearanceInfo.securityClearance ? 'bg-success' : 'bg-danger'">
                        {{ selectedTermination.clearanceInfo.securityClearance ? 'Cleared' : 'Pending' }}
                      </span>
                    </div>
                    <small class="text-muted">Returned ID badge, security tokens, etc.</small>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-12">
                  <div class="card bg-light">
                    <div class="card-header">
                      <h6 class="mb-0">Clearance Notes</h6>
                    </div>
                    <div class="card-body">
                      <p v-if="selectedTermination.clearanceInfo.clearanceNotes">
                        {{ selectedTermination.clearanceInfo.clearanceNotes }}
                      </p>
                      <p v-else class="text-muted">No clearance notes available.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="text-center p-4">
              <div class="alert alert-warning">No clearance information available for this termination.</div>
            </div>
          </div>
          <div class="modal-footer">
            <button
                v-if="selectedTermination && selectedTermination.status && selectedTermination.status.toLowerCase() === 'pending'"
                type="button"
                class="btn btn-primary me-2"
                @click="editClearance(selectedTermination)"
            >
              Edit Clearance
            </button>
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Clearance Modal -->
    <div class="modal fade sep-term-bs-modal" id="sep-term-edit-clearance-modal" tabindex="-1" aria-labelledby="editClearanceModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-primary text-white">
            <h5 class="modal-title" id="editClearanceModalLabel">Update Clearance Status</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <form id="updateClearanceForm" @submit.prevent="updateClearance">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <div class="form-check">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          id="editItClearance"
                          v-model="editClearanceData.itClearance"
                      >
                      <label class="form-check-label" for="editItClearance">
                        IT Department Clearance
                      </label>
                    </div>
                    <small class="text-muted">Returned all company devices, access cards, etc.</small>
                  </div>

                  <div class="mb-3">
                    <div class="form-check">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          id="editFinanceClearance"
                          v-model="editClearanceData.financeClearance"
                      >
                      <label class="form-check-label" for="editFinanceClearance">
                        Finance Department Clearance
                      </label>
                    </div>
                    <small class="text-muted">Settled all pending accounts, advances, etc.</small>
                  </div>

                  <div class="mb-3">
                    <div class="form-check">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          id="editHrClearance"
                          v-model="editClearanceData.hrClearance"
                      >
                      <label class="form-check-label" for="editHrClearance">
                        HR Department Clearance
                      </label>
                    </div>
                    <small class="text-muted">Completed exit interview, handover notes, etc.</small>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="mb-3">
                    <div class="form-check">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          id="editAdminClearance"
                          v-model="editClearanceData.adminClearance"
                      >
                      <label class="form-check-label" for="editAdminClearance">
                        Admin Department Clearance
                      </label>
                    </div>
                    <small class="text-muted">Returned office keys, parking cards, etc.</small>
                  </div>

                  <div class="mb-3">
                    <div class="form-check">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          id="editDeptClearance"
                          v-model="editClearanceData.deptClearance"
                      >
                      <label class="form-check-label" for="editDeptClearance">
                        Department Head Clearance
                      </label>
                    </div>
                    <small class="text-muted">Completed knowledge transfer, handover, etc.</small>
                  </div>

                  <div class="mb-3">
                    <div class="form-check">
                      <input
                          class="form-check-input"
                          type="checkbox"
                          id="editSecurityClearance"
                          v-model="editClearanceData.securityClearance"
                      >
                      <label class="form-check-label" for="editSecurityClearance">
                        Security Department Clearance
                      </label>
                    </div>
                    <small class="text-muted">Returned ID badge, security tokens, etc.</small>
                  </div>
                </div>

                <div class="col-12 mt-3">
                  <div class="mb-3">
                    <label for="editClearanceNotes" class="form-label">Clearance Notes</label>
                    <textarea
                        class="form-control"
                        id="editClearanceNotes"
                        rows="3"
                        placeholder="Enter any specific notes about clearance status"
                        v-model="editClearanceData.clearanceNotes"
                    ></textarea>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
            <button
                type="button"
                class="btn btn-primary"
                :disabled="isUpdatingClearance"
                @click="updateClearance"
            >
              <span v-if="isUpdatingClearance" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Update Clearance
            </button>
          </div>
        </div>
      </div>
    </div>
      </Teleport>
  </div>
</template>

<script>
/* eslint-disable */
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, uploads } from "@/api";

export default {
  name: "SeparationTermination",
  components: { HrPageLayout },
  data() {
    return {
      date: '',
      keyword: '',
      dateFrom: '',
      dateTo: '',
      selectedMonth: '',
      selectedDepartment: '',
      terminationsData: [],
      statistics: {
        totalTerminations: 0,
        resignations: 0,
        dismissals: 0,
        transfers: 0,
        deaths: 0,
        others: 0,
        pending: 0
      },
      employees: [],
      // For create-termination modal
      showCreateTerminationModal: false,
      selectedEmployeeId: '',
      selectedEmployeeDetails: null,
      departments: [],
      isLoading: false,
      isApproving: null,
      isRejecting: null,
      isProcessingAction: false,
      isUpdatingClearance: false,
      message: '',
      confirmationModal: null,
      documentsModal: null,
      clearanceModal: null,
      editClearanceModal: null,
      selectedTerminationId: null,
      confirmationType: null,
      selectedTermination: null,
      currentDocuments: [],
      editClearanceData: {
        itClearance: false,
        financeClearance: false,
        hrClearance: false,
        adminClearance: false,
        deptClearance: false,
        securityClearance: false,
        clearanceNotes: ''
      }
    }
  },

  mounted() {
    // First fetch departments and employees, then fetch terminations
    this.getDepartments();
    this.getEmployeesList().then(() => {
      this.getTerminationsData();
    });

    // Other modals still use Bootstrap; create-termination uses pure Vue
  },

  methods: {
    // Get terminations data
    getTerminationsData() {
      this.isLoading = true;
      const data = new FormData();
      data.append("function", "getTerminations");

      // Add filters if selected
      if (this.selectedMonth) {
        data.append("month", this.selectedMonth);
      }
      if (this.selectedDepartment) {
        data.append("department", this.selectedDepartment);
      }
      if (this.keyword) {
        data.append("keyword", this.keyword);
      }

      execute(data, authUrl)
          .then((res) => {
            this.isLoading = false;
            if (res.data.success) {
              // Check if data exists and has the right structure based on the provided JSON
              if (res.data.data && Array.isArray(res.data.data.terminations)) {
                // Update statistics from the metrics in the response
                if (res.data.data.metrics) {
                  this.statistics = {
                    totalTerminations: res.data.data.metrics.totalTerminations || 0,
                    resignations: res.data.data.metrics.resignationTerminations || 0,
                    dismissals: res.data.data.metrics.dismissalTerminations || 0,
                    transfers: res.data.data.metrics.transferTerminations || 0,
                    deaths: res.data.data.metrics.deathTerminations || 0,
                    others: res.data.data.metrics.otherTerminations || 0,
                    pending: res.data.data.metrics.pendingTerminations || 0
                  };
                }

                this.terminationsData = res.data.data.terminations.map((item) => {
                  // Parse the documents JSON string
                  let parsedDocuments = [];
                  try {
                    if (item.documents) {
                      parsedDocuments = JSON.parse(item.documents);
                    }
                  } catch (e) {
                    console.error("Error parsing documents:", e);
                  }

                  // Parse clearance info if it exists
                  let parsedClearanceInfo = null;
                  try {
                    if (item.clearanceInfo) {
                      parsedClearanceInfo = JSON.parse(item.clearanceInfo);
                    } else {
                      // Default clearance structure if none exists
                      parsedClearanceInfo = {
                        itClearance: false,
                        financeClearance: false,
                        hrClearance: false,
                        adminClearance: false,
                        deptClearance: false,
                        securityClearance: false,
                        clearanceNotes: ''
                      };
                    }
                  } catch (e) {
                    console.error("Error parsing clearance info:", e);
                    parsedClearanceInfo = {
                      itClearance: false,
                      financeClearance: false,
                      hrClearance: false,
                      adminClearance: false,
                      deptClearance: false,
                      securityClearance: false,
                      clearanceNotes: ''
                    };
                  }

                  return {
                    id: item.id,
                    employeeId: item.employeeId,
                    employeeName: item.employeeName || "N/A",
                    separationType: item.separationType || "N/A",
                    terminationDate: item.separationDate || "N/A",
                    lastWorkingDay: item.lastWorkingDay || "N/A",
                    description: item.description || "N/A",
                    documents: parsedDocuments,
                    clearanceInfo: parsedClearanceInfo,
                    departmentName: item.departmentName || "N/A",
                    departmentID: item.departmentID || "",
                    status: item.status || "pending",
                    approvedBy: item.approvedBy || null,
                    approvedAt: item.approvedAt || null,
                    rejectedBy: item.rejectedBy || null,
                    rejectedAt: item.rejectedAt || null,
                    createdAt: item.createdAt,
                    updatedAt: item.updatedAt
                  };
                });
              } else {
                this.terminationsData = [];
                console.log("No terminations data found or incorrect data format");
              }
            } else {
              this.message = res.data.message;
              this.terminationsData = [];
            }
          })
          .catch((e) => {
            this.isLoading = false;
            this.message = e.message;
            this.terminationsData = [];
          });
    },

    // Get terminations statistics
    getTerminationsStatistics() {
      // This function is now redundant since we get metrics in the getTerminations call
      // But we'll keep it as a fallback in case we need to fetch stats separately
      const data = new FormData();
      data.append("function", "getTerminationStats");

      // Add filters if selected
      if (this.selectedMonth) {
        data.append("month", this.selectedMonth);
      }
      if (this.selectedDepartment) {
        data.append("department", this.selectedDepartment);
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              if (res.data.data && res.data.data.metrics) {
                this.statistics = {
                  totalTerminations: res.data.data.metrics.totalTerminations || 0,
                  resignations: res.data.data.metrics.resignationTerminations || 0,
                  dismissals: res.data.data.metrics.dismissalTerminations || 0,
                  transfers: res.data.data.metrics.transferTerminations || 0,
                  deaths: res.data.data.metrics.deathTerminations || 0,
                  others: res.data.data.metrics.otherTerminations || 0,
                  pending: res.data.data.metrics.pendingTerminations || 0
                };
              } else {
                // Default statistics if API doesn't return the expected structure
                this.statistics = {
                  totalTerminations: 0,
                  resignations: 0,
                  dismissals: 0,
                  transfers: 0,
                  deaths: 0,
                  others: 0,
                  pending: 0
                };
              }
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.message = e.message;
          });
    },

    // Get employees list for dropdown
    async getEmployeesList() {
      const data = new FormData();
      data.append("function", "getEmployees");
      data.append("status", "Active");

      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          const list = Array.isArray(res.data.data?.employees) ? res.data.data.employees : [];
          // Show all employees returned by API
          this.employees = list.map(employee => {
            const middleName = employee.middleName ? ` ${employee.middleName} ` : ' ';
            return {
              id: employee.id,
              name: `${employee.firstName}${middleName}${employee.lastName}`,
              value: employee.id,
              department: employee.departmentID,
              designation: employee.designation
            };
          });
        } else {
          this.message = res.data.message;
        }
      } catch (e) {
        this.message = e.message;
      }
    },

    // Handle create termination form submission
    submitTermination(event) {
      event.preventDefault();

      const form = event.target;
      const employeeId = form.selectEmployee.value;
      const separationType = form.separationType.value;
      const separationDate = form.separationDate.value;
      const lastWorkingDay = form.lastWorkingDay.value;
      const description = form.description.value;
      const documents = form.uploadDocuments.files;

      // Get clearance information
      const clearanceInfo = {
        itClearance: form.itClearance ? form.itClearance.checked : false,
        financeClearance: form.financeClearance ? form.financeClearance.checked : false,
        hrClearance: form.hrClearance ? form.hrClearance.checked : false,
        adminClearance: form.adminClearance ? form.adminClearance.checked : false,
        deptClearance: form.deptClearance ? form.deptClearance.checked : false,
        securityClearance: form.securityClearance ? form.securityClearance.checked : false,
        clearanceNotes: form.clearanceNotes ? form.clearanceNotes.value : ''
      };

      if (!employeeId || !separationType || !separationDate || !lastWorkingDay) {
        this.message = "Please fill all required fields";
        return;
      }

      this.isLoading = true;

      // Find the selected employee to include their details
      const selectedEmployee = this.employees.find(emp => String(emp.id) === String(employeeId));

      const data = new FormData();
      data.append("function", "createTermination");
      data.append("employeeId", employeeId);
      data.append("employeeName", selectedEmployee ? selectedEmployee.name : "");
      data.append("separationType", separationType);
      data.append("separationDate", separationDate);
      data.append("lastWorkingDay", lastWorkingDay);
      data.append("description", description);
      data.append("clearanceInfo", JSON.stringify(clearanceInfo));

      // Include department info if available
      if (selectedEmployee && selectedEmployee.department) {
        data.append("departmentId", selectedEmployee.department);
      }

      // Include designation if available
      if (selectedEmployee && selectedEmployee.designation) {
        data.append("designation", selectedEmployee.designation);
      }

      // Append documents if any
      if (documents.length > 0) {
        for (let i = 0; i < documents.length; i++) {
          data.append("documents[]", documents[i]);
        }
      }

      execute(data, authUrl)
          .then((res) => {
            this.isLoading = false;
            if (res.data.success) {
              this.closeAllModals();
              this.getTerminationsData();
              this.message = res.data.message || "Termination created successfully";
              form.reset();
              this.selectedEmployeeId = "";
              this.selectedEmployeeDetails = null;
            } else {
              this.message = res.data.message || "Failed to create termination. Please try again.";
            }
          })
          .catch((e) => {
            this.isLoading = false;
            this.message = e.message || "An error occurred. Please try again.";
          });
    },

    closeAllModals() {
      // Pure Vue: just hide the create-termination modal flag
      this.showCreateTerminationModal = false;
    },

    onEmployeeChange() {
      this.selectedEmployeeDetails =
          this.employees.find(emp => String(emp.id) === String(this.selectedEmployeeId)) || null;
    },

    // Handle month selection change
    handleMonthChange(event) {
      this.selectedMonth = event.target.value;
      this.getTerminationsData();
    },

    // Get departments list
    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              if (res.data.data && Array.isArray(res.data.data.departments)) {
                this.departments = res.data.data.departments;
              } else {
                console.log("No departments data found or incorrect data format");
              }
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.message = e.message;
          });
    },

    // Handle department selection change
    handleDepartmentChange(event) {
      this.selectedDepartment = event.target.value;
      this.getTerminationsData();
    },

    // Handle search input
    handleSearch(event) {
      this.keyword = event.target.value;
      this.getTerminationsData();
    },

    getStatusBadgeClass(status) {
      if (!status) return 'hr-badge-warning';
      const s = status.toLowerCase();
      if (s === 'approved') return 'hr-badge-success';
      if (s === 'rejected') return 'hr-badge-danger';
      return 'hr-badge-warning';
    },

    getSeparationTypeBadgeClass(type) {
      if (!type) return 'hr-badge-secondary';
      if (type === 'Resignation') return 'hr-badge-success';
      if (type === 'Death') return 'hr-badge-danger';
      if (type === 'Transfers') return 'hr-badge-primary';
      if (type === 'Dismissal') return 'hr-badge-warning';
      return 'hr-badge-secondary';
    },

    openAddTerminationModal() {
      this.showCreateTerminationModal = true;
    },

    closeAddTerminationModal() {
      this.showCreateTerminationModal = false;
    },

    openDownloadModal() {
      const el = document.querySelector('.download-modal');
      if (el && typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const modal = new bootstrap.Modal(el);
        modal.show();
      }
    },

    // Format status text for display
    formatStatus(status) {
      if (!status) return 'Pending';

      const statusLower = status.toLowerCase();
      if (statusLower === 'pending') return 'Pending';
      return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    },

    // Show confirmation modal before approval/rejection
    showConfirmationModal(id, type) {
      this.selectedTerminationId = id;
      this.confirmationType = type;

      // Find the termination data to display in confirmation
      this.selectedTermination = this.terminationsData.find((item) => String(item.id) === String(id));

      // Show modal
      if (this.confirmationModal) {
        this.confirmationModal.show();
      } else {
        this.confirmationModal = new bootstrap.Modal(document.getElementById("sep-term-confirmation-modal"));
        this.confirmationModal.show();
      }
    },

    // Confirm action (approve or reject)
    confirmAction() {
      this.isProcessingAction = true;

      if (this.confirmationType === 'approve') {
        this.approveTermination(this.selectedTerminationId);
      } else if (this.confirmationType === 'reject') {
        this.rejectTermination(this.selectedTerminationId);
      }
    },

    // Approve termination
    approveTermination(id) {
      const data = new FormData();
      data.append("function", "approveTermination");
      data.append("terminationId", id);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.message = res.data.message || "Termination successfully approved";
              if (this.confirmationModal) {
                this.confirmationModal.hide();
              }
              this.getTerminationsData();
            } else {
              this.message = res.data.message || "Failed to approve termination";
            }
            this.isProcessingAction = false;
          })
          .catch((e) => {
            this.isProcessingAction = false;
            this.message = e.message || "An error occurred while approving";

            // Close confirmation modal
            if (this.confirmationModal) {
              this.confirmationModal.hide();
            }
          });
    },

    // Reject termination
    rejectTermination(id) {
      const data = new FormData();
      data.append("function", "rejectTermination");
      data.append("terminationId", id);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.message = res.data.message || "Termination successfully rejected";
              if (this.confirmationModal) {
                this.confirmationModal.hide();
              }
              this.getTerminationsData();
            } else {
              this.message = res.data.message || "Failed to reject termination";
            }
            this.isProcessingAction = false;
          })
          .catch((e) => {
            this.isProcessingAction = false;
            this.message = e.message || "An error occurred while rejecting";

            // Close confirmation modal
            if (this.confirmationModal) {
              this.confirmationModal.hide();
            }
          });
    },

    // Format date for display
    formatDate(dateString) {
      if (!dateString) return "N/A";
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    // Format date with time
    formatDateWithTime(dateTimeString) {
      if (!dateTimeString) return "N/A";
      const date = new Date(dateTimeString);
      return `${date.toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      })} ${date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
    },

    documentUrl(path) {
      if (!path) return "#";
      const s = String(path).trim();
      if (/^https?:\/\//i.test(s)) return s;
      const base = String(uploads || "").replace(/\/$/, "");
      const rel = s.replace(/^\//, "");
      return `${base}/${rel}`;
    },

    // View documents in modal
    viewDocuments(documents) {
      this.currentDocuments = documents;

      if (this.documentsModal) {
        this.documentsModal.show();
      } else {
        this.documentsModal = new bootstrap.Modal(document.getElementById("sep-term-documents-modal"));
        this.documentsModal.show();
      }
    },

    // View clearance status
    viewClearanceStatus(termination) {
      this.selectedTermination = termination;

      if (this.clearanceModal) {
        this.clearanceModal.show();
      } else {
        this.clearanceModal = new bootstrap.Modal(document.getElementById("sep-term-clearance-modal"));
        this.clearanceModal.show();
      }
    },

    // Calculate overall clearance percentage
    calculateOverallClearance(clearanceInfo) {
      if (!clearanceInfo) return 0;

      const keys = ['itClearance', 'financeClearance', 'hrClearance', 'adminClearance', 'deptClearance', 'securityClearance'];
      const total = keys.length;
      let completed = 0;

      keys.forEach(key => {
        if (clearanceInfo[key]) {
          completed += 1;
        }
      });

      return Math.round((completed / total) * 100);
    },

    // Edit clearance info
    editClearance(termination) {
      // Close the current clearance modal
      if (this.clearanceModal) {
        this.clearanceModal.hide();
      }

      // Initialize the edit data with the current values
      this.editClearanceData = { ...termination.clearanceInfo };
      this.selectedTermination = termination;

      // Show the edit modal
      if (this.editClearanceModal) {
        this.editClearanceModal.show();
      } else {
        this.editClearanceModal = new bootstrap.Modal(document.getElementById("sep-term-edit-clearance-modal"));
        this.editClearanceModal.show();
      }
    },

    // Update clearance info
    updateClearance() {
      this.isUpdatingClearance = true;

      const data = new FormData();
      data.append("function", "updateClearance");
      data.append("terminationId", this.selectedTermination.id);
      data.append("clearanceInfo", JSON.stringify(this.editClearanceData));

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Update local data
              const index = this.terminationsData.findIndex(item => item.id === this.selectedTermination.id);
              if (index !== -1) {
                this.terminationsData[index].clearanceInfo = { ...this.editClearanceData };
                this.selectedTermination = this.terminationsData[index];
              }

              this.message = "Clearance information updated successfully";

              // Close edit modal and show clearance modal again
              if (this.editClearanceModal) {
                this.editClearanceModal.hide();
              }

              // Show clearance modal again
              setTimeout(() => {
                if (this.clearanceModal) {
                  this.clearanceModal.show();
                }
              }, 500);
            } else {
              this.message = res.data.message || "Failed to update clearance information";
            }

            this.isUpdatingClearance = false;
          })
          .catch((e) => {
            this.isUpdatingClearance = false;
            this.message = e.message || "An error occurred while updating clearance";

            // Close edit modal
            if (this.editClearanceModal) {
              this.editClearanceModal.hide();
            }
          });
    },

    // Generate clearance report
    generateClearanceReport() {
      // This would typically make an API call to generate a report
      // For now, we'll just show a message
      this.message = "Generating clearance report... This feature is coming soon!";

      // In real implementation, call API:
      /*
      const data = new FormData();
      data.append("function", "generateClearanceReport");

      if (this.selectedMonth) {
        data.append("month", this.selectedMonth);
      }
      if (this.selectedDepartment) {
        data.append("department", this.selectedDepartment);
      }

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Handle success, e.g., open report in new window or download
              window.open(res.data.data.reportUrl, '_blank');
              this.message = "Report generated successfully";
            } else {
              this.message = res.data.message || "Failed to generate report";
            }
          })
          .catch((e) => {
            this.message = e.message || "An error occurred while generating report";
          });
      */
    }
  }
}
</script>
<style src="@/assets/css/hr-page-shared.css"></style>
<style>
/* Teleported modals: backdrop must sit under dialog (Bootstrap default can lose to nav) */
body > .modal-backdrop.show {
  z-index: 20050 !important;
}
</style>
<style scoped>
.sep-term-bs-modal {
  z-index: 20060 !important;
}
.sep-month-select { min-width: 140px; margin-right: 8px; }
.sep-toast { position: fixed; bottom: 20px; right: 20px; z-index: 1100; padding: 12px 20px; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); display: flex; align-items: center; gap: 12px; max-width: 400px; }
.sep-toast.success { background: #10b981; color: #fff; }
.sep-toast.error { background: #ef4444; color: #fff; }
.sep-toast-close { background: none; border: none; color: inherit; font-size: 20px; cursor: pointer; padding: 0 4px; }
.sep-filters { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; flex: 1; }
.sep-filters .hr-search-wrap { min-width: 200px; }
.hr-flex-between { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px; }
.hr-loading-wrap { text-align: center; padding: 3rem; }
.hr-spinner { width: 40px; height: 40px; border: 3px solid rgba(91,115,232,0.2); border-top-color: #5b73e8; border-radius: 50%; animation: sep-spin 0.8s linear infinite; margin: 0 auto 1rem; }
.sep-cell-truncate { max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sep-doc-cell { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.sep-actions-cell { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.hr-small { font-size: 0.85rem; }
.hr-mt-1 { margin-top: 4px; }
.hr-py-4 { padding-top: 16px; padding-bottom: 16px; }
.hr-table-striped tbody tr:nth-child(even) { background: #f8fafc; }
.hr-table-hover tbody tr:hover { background: rgba(91,115,232,0.05); }
@keyframes sep-spin { to { transform: rotate(360deg); } }
</style>